import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";

const emptyStateFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

const promoReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.99);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const promoButtonShimmer = keyframes`
  0%, 58% {
    transform: translate3d(-180%, 0, 0) skewX(-18deg);
  }
  82%, 100% {
    transform: translate3d(520%, 0, 0) skewX(-18deg);
  }
`;

export const SeoOnlyHeading = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/* ─── Home promo banner ────────────────────────────────────────────────── */

export const PromoBannerSection = styled.section`
  position: relative;
  width: min(1080px, calc(100% - 80px));
  margin: 34px auto 0;
  z-index: 2;
  animation: ${promoReveal} 620ms var(--ease-snappy) both;

  @media (max-width: 720px) {
    width: calc(100% - 32px);
    margin-top: 18px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PromoBannerMedia = styled.video`
  position: absolute;
  inset: 0;
  right: auto;
  display: block;
  width: 110%;
  height: 100%;
  min-width: 100%;
  min-height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  transform: scale(1);
  transition: transform 800ms var(--ease-snappy);
  pointer-events: none;
  z-index: 0;

  @media (max-width: 720px) {
    width: 100%;
    object-position: 38% 50%;
    transform: scale(1);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PromoBannerSurface = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 260px;
  padding: 38px 42px;
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: var(--radius-xl);
  overflow: hidden;
  isolation: isolate;
  background: var(--color-brand-900);
  box-shadow:
    0 28px 56px -22px rgba(var(--color-brand-900-rgb), 0.56),
    0 10px 24px rgba(var(--color-brand-600-rgb), 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.28);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: #001435;
    opacity: 0.6;
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.7);
    box-shadow:
      0 28px 52px -20px rgba(0, 0, 0, 0.68),
      0 8px 20px rgba(0, 0, 0, 0.32);
  }

  &:hover ${PromoBannerMedia} {
    transform: scale(1.02);
  }

  @media (max-width: 720px) {
    display: flex;
    align-items: flex-end;
    min-height: 280px;
    padding: 28px 14px 12px;
    border-radius: var(--radius-lg);

    &:hover ${PromoBannerMedia} {
      transform: scale(1.01);
    }
  }
`;

export const PromoBannerContent = styled.div`
  position: relative;
  z-index: 2;
  width: min(72%, 760px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 900px) {
    width: 100%;
  }

  @media (max-width: 720px) {
    width: 100%;
  }
`;

export const PromoTitle = styled.h2`
  max-width: none;
  margin: 0;
  color: #f4f9ff;
  font-family: var(--font-family-primary);
  font-size: clamp(24px, 3.4vw, 40px);
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1.28;
  text-shadow: 0 3px 18px rgba(0, 0, 0, 0.42);

  span {
    display: block;
    width: 100%;
    white-space: nowrap;
    text-align: justify;
    text-align-last: justify;
  }

  @media (max-width: 720px) {
    font-size: clamp(15.5px, calc(5.8vw - 3px), 24px);
    letter-spacing: -0.06em;
    line-height: 1.36;
  }
`;

export const PromoText = styled.p`
  max-width: 54ch;
  margin: 12px 0 0;
  color: rgba(238, 247, 255, 0.88);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.012em;
  line-height: 1.45;

  @media (max-width: 720px) {
    margin-top: 8px;
  }
`;

export const PromoAction = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  margin-top: 20px;
  padding: 0 22px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: var(--radius-pill);
  overflow: hidden;
  isolation: isolate;
  background: rgba(247, 251, 255, 0.96);
  color: var(--color-brand-700);
  box-shadow:
    0 14px 30px rgba(0, 0, 0, 0.28),
    0 4px 12px rgba(var(--color-brand-400-rgb), 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 1000;
  letter-spacing: 0.01em;
  padding: 0 22px 0 12[];
  -webkit-text-stroke: 0.28px var(--color-brand-700);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.72);
  cursor: pointer;
  transition:
    transform 190ms var(--ease-snappy),
    filter 190ms var(--ease-snappy),
    box-shadow 190ms var(--ease-snappy);

  &::after {
    content: "";
    position: absolute;
    top: -45%;
    bottom: -45%;
    left: 0;
    width: 22%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.25) 18%,
      rgba(255, 255, 255, 0.92) 50%,
      rgba(var(--color-brand-200-rgb), 0.38) 72%,
      transparent 100%
    );
    filter: blur(1px);
    pointer-events: none;
    z-index: 2;
    animation: ${promoButtonShimmer} 8s var(--ease-snappy) infinite;
  }

  svg {
    width: 18px;
    height: 18px;

    path {
      stroke: var(--color-brand-600);
    }
  }

  &:hover {
    transform: translateY(-2px);
    background: #f4f9ff;
    box-shadow:
      0 18px 34px rgba(0, 0, 0, 0.32),
      0 6px 16px rgba(var(--color-brand-400-rgb), 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 1);
  }

  &:focus-visible {
    outline: 3px solid var(--color-brand-glow);
    outline-offset: 3px;
  }

  &:active {
    transform: translateY(0) scale(0.985);
  }

  @media (max-width: 720px) {
    margin-top: 12px;
  }

  @media (max-width: 520px) {
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      animation: none;
      display: none;
    }
  }
`;

/* ─── Page heading ───────────────────────────────────────────────────────── */

export const PageHeadingWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  padding: 40px 24px 12px;
  max-width: 960px;
  margin: 0 auto;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 28px 0px 8px;
    gap: 10px;
  }
`;

export const PageHeadingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 6px 12px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-line-strong);
  background: linear-gradient(
    180deg,
    rgba(var(--glass-white-rgb), 0.95) 0%,
    rgba(var(--glass-pale-rgb), 0.85) 100%
  );
  box-shadow: var(--shadow-liquid);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-brand-500);
    box-shadow: 0 0 10px rgba(var(--color-brand-400-rgb), 0.8);
  }
`;

export const PageHeading = styled.h1`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: clamp(34px, 5vw, 56px);
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.035em;
  text-align: center;
  max-width: 18ch;

  /* Brand gradient text + subtle drop-shadow glow */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 2px 4px rgba(var(--color-brand-600-rgb), 0.14))
    drop-shadow(0 1px 2px rgba(var(--glass-white-rgb), 0.7));

  @media (max-width: 768px) {
    max-width: 100%;
    letter-spacing: -0.03em;
  }
`;

/* Accent span — the prominent gradient phrase ("4K Quality", etc.) */
export const PageHeadingAccent = styled.span`
  display: inline-block;
  background: linear-gradient(
    90deg,
    var(--color-brand-400) 0%,
    var(--color-brand-600) 50%,
    var(--color-brand-700) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 4px 10px rgba(var(--color-brand-600-rgb), 0.35))
    drop-shadow(0 1px 2px rgba(var(--color-brand-400-rgb), 0.24));
`;

export const PageHeadingSubtitle = styled.p`
  margin: 0;
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 6px 14px;
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-muted);
  letter-spacing: 0.01em;

  span {
    display: inline-flex;
    text-transform: uppercase;
    align-items: center;
    gap: 12px;
  }

  /* Real centered dot — sits at the optical center between two spans */
  span:not(:first-child)::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--color-brand-500);
    box-shadow: 0 0 6px rgba(var(--color-brand-400-rgb), 0.45);
  }

  /* Only the first span gets the brand gradient treatment */
  span:first-child {
    font-weight: 800;
    letter-spacing: 0.04em;
    background: linear-gradient(
      90deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 50%,
      var(--color-brand-700) 100%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.2));
  }
`;

/* ─── Intro / hero section ──────────────────────────────────────────────── */

export const IntroSection = styled.section`
  position: relative;
  padding: 24px 12px 16px;

  @media (max-width: 768px) {
    padding: 20px 8px 14px;
  }
`;

export const IntroCard = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1.45fr 1fr;
  gap: 24px;
  padding: 28px;
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-line);
  background:
    radial-gradient(
      circle at top left,
      rgba(var(--color-brand-300-rgb), 0.45),
      transparent 30%
    ),
    linear-gradient(
      145deg,
      rgba(var(--glass-white-rgb), 0.88),
      rgba(var(--glass-pale-rgb), 0.78)
    );
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-panel);
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    bottom: -18px;
    right: -24px;
    width: 220px;
    height: 160px;
    background: url("/decor/drops-b.png") center/contain no-repeat;
    opacity: 0.5;
    pointer-events: none;
    filter: drop-shadow(0 14px 28px rgba(var(--color-brand-400-rgb), 0.28));
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    padding: 22px;
    border-radius: var(--radius-lg);

    &::after {
      width: 150px;
      height: 110px;
      opacity: 0.4;
    }
  }
`;

export const IntroCopy = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Eyebrow = styled.p`
  margin: 0;
  color: var(--color-brand-700);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 800;
`;

export const HeroTitle = styled.h1`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: clamp(2rem, 3.2vw, 3.4rem);
  line-height: normal;
  letter-spacing: -0.05em;
`;

export const HeroText = styled.p`
  margin: 0;
  color: var(--color-text-body);
  line-height: normal;
  max-width: 820px;
`;

export const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

const actionStyles = `
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 42px;
  padding: 0 20px;
  border-radius: 999px;
  text-decoration: none;
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
`;

export const PrimaryLink = styled(Link)`
  ${actionStyles}
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow: var(--shadow-cta);

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const SecondaryLink = styled(Link)`
  ${actionStyles}
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-brand-700);

  &:hover {
    border-color: var(--color-line-strong);
  }
`;

export const IntroAside = styled.aside`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px;
  border-radius: var(--radius-lg);
  background: var(--glass-base-2);
  border: 1px solid var(--color-line);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  box-shadow: var(--shadow-liquid);
`;

export const AsideTitle = styled.h2`
  position: relative;
  margin: 0;
  padding-bottom: 16px;
  font-family: var(--font-family-primary);
  font-size: 1.15rem;
  color: var(--color-text-strong);
  font-weight: 700;

  /* Smaller splash-trail for sidebar sections */
  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 120px;
    height: 14px;
    background: url("/decor/splash-trail.webp") left center/contain no-repeat;
    opacity: 0.55;
    filter: drop-shadow(0 1px 3px rgba(var(--color-brand-400-rgb), 0.22));
  }
`;

export const AsideList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: var(--color-text-body);
  line-height: normal;
`;

export const KeywordCloud = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const KeywordLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  color: var(--color-brand-700);
  background: var(--color-brand-mist);
  border: 1px solid var(--color-line);
  text-decoration: none;
  font-size: 0.92rem;
  font-weight: 600;
  transition:
    background 180ms var(--ease-snappy),
    transform 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy);

  &:hover {
    background: rgba(var(--color-brand-300-rgb), 0.65);
    transform: translateY(-1px);
    box-shadow: var(--shadow-liquid);
  }
`;

/* ─── Guides section ───────────────────────────────────────────────────── */

export const GuidesSection = styled.section`
  padding: 4px 12px 18px;

  @media (max-width: 768px) {
    padding: 4px 8px 16px;
  }
`;

export const GuidesCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  border-radius: var(--radius-xl);
  background: var(--color-surface-1);
  border: 1px solid var(--color-line);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  box-shadow: var(--shadow-panel);
`;

export const GuidesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
`;

export const GuidesHeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 760px;
`;

export const GuidesTitle = styled.h2`
  position: relative;
  margin: 0;
  padding-bottom: 22px;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: clamp(1.5rem, 2.4vw, 2.2rem);
  line-height: normal;
  font-weight: 800;
  letter-spacing: -0.04em;

  /* splash-trail underline — aquatic accent beneath the section title */
  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 180px;
    height: 20px;
    background: url("/decor/splash-trail.webp") left center/contain no-repeat;
    opacity: 0.7;
    filter: drop-shadow(0 2px 4px rgba(var(--color-brand-400-rgb), 0.22));
  }

  @media (max-width: 768px) {
    padding-bottom: 18px;
    &::after {
      width: 140px;
      height: 16px;
    }
  }
`;

export const GuidesText = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  line-height: normal;
`;

export const GuidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1120px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const GuideCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: var(--radius-lg);
  background: var(--color-surface-2);
  border: 1px solid var(--color-line);
  box-shadow: var(--shadow-liquid);
  transition:
    transform 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-2px);
    border-color: var(--color-line-strong);
    box-shadow: var(--shadow-glow), var(--shadow-liquid);
  }
`;

export const GuideMeta = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.9rem;
  font-weight: 500;
`;

export const GuideTitleLink = styled(Link)`
  color: var(--color-text-strong);
  text-decoration: none;
  font-family: var(--font-family-primary);
  font-size: 1.08rem;
  line-height: normal;
  font-weight: 700;
  transition: color 180ms var(--ease-snappy);

  &:hover {
    color: var(--color-brand-700);
  }
`;

export const GuideExcerpt = styled.p`
  margin: 0;
  color: var(--color-text-body);
  line-height: normal;
`;

/* ─── Main content / feed layout ────────────────────────────────────────── */

export const ContentContainer = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  padding: 0 12px;
  overflow: visible;
  z-index: 1;

  position: relative;
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;
export const WaterSeparator = styled.img`
  position: absolute;
  right: 0;
  display: block;
  width: 100%;
  max-width: none;
  height: auto;
  opacity: 0.5;
  pointer-events: none;

  z-index: 1;
  top: -80px;
  @media (max-width: 768px) {
    top: -20px;
  }
`;

/* Toolbar — single wrapper that aligns CreateInputBar / TabsComponent /
   CollectionFilter at the same horizontal width and padding. */
export const Toolbar = styled.div`
  position: relative;
  width: 100%;
  max-width: 1080px;
  margin: 20px auto 0;
  padding: 0 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 3;
  margin-bottom: ${(p) => (p.$feed ? "0px" : "20px")};

  @media (max-width: 768px) {
    margin: ${(p) => (p.$feed ? "0" : "16px auto 0")};
    padding: 0 12px;
    margin-bottom: ${(p) => (p.$feed ? "0px" : "16px")};
  }
  @media (max-width: 400px) {
    margin-bottom: 0;
  }
`;

export const PaginationWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 32px 0;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

export const Content = styled.div`
  display: flex;
  flex: 1;
  gap: 12px;
  z-index: 3;
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

export const FeedList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100dvh - 150px);
  /* margin-top: 110px; */
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  @media (max-width: 768px) {
    height: calc(100dvh - 72px);
    /* margin-top: 56px; */
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FeedSentinel = styled.div`
  width: 100%;
  height: 1px;
  pointer-events: none;
  flex-shrink: 0;
`;

export const FeedSkeletonList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100dvh - 150px);
  /* margin-top: 110px; */
  z-index: 4;
  overflow: hidden;

  @media (max-width: 768px) {
    height: calc(100dvh - 72px);
    /* margin-top: 56px; */
  }
`;

export const FeedSkeleton = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  padding: 0 16px 18px;
  display: flex;
  align-items: stretch;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0 8px 12px;
  }
`;

export const FeedSkeletonFrame = styled.div`
  /* Static silhouette only. The previous version animated
     background-position on a ::before, which forces a full-frame repaint
     on every tick — unnecessary for a brief loading state. */
  position: relative;
  width: min(100%, 470px);
  aspect-ratio: 9 / 16;
  height: auto;
  max-height: 100%;
  align-self: center;
  overflow: hidden;
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-line);
  z-index: 5;
  background: linear-gradient(
    180deg,
    rgba(var(--glass-pale-rgb), 0.85) 0%,
    rgba(var(--glass-pale-rgb), 0.9) 100%
  );
  box-shadow: var(--shadow-liquid);

  @media (max-width: 768px) {
    aspect-ratio: auto;
    height: 100%;
    max-height: none;
    align-self: stretch;
    border-radius: var(--radius-lg);
  }
`;

const feedDotPulse = `
  @keyframes feedDotPulse {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
    40% { transform: scale(1); opacity: 1; }
  }
`;

export const FeedLoader = styled.div`
  ${feedDotPulse}
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 18px 0 24px;

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--color-brand-500);
    box-shadow: 0 0 12px rgba(var(--color-brand-400-rgb), 0.55);
    animation: feedDotPulse 1.1s ease-in-out infinite both;
  }

  span:nth-child(1) {
    animation-delay: -0.32s;
  }
  span:nth-child(2) {
    animation-delay: -0.16s;
  }
`;

export const FeedEndMarker = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 18px 0 28px;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.7;
`;

export const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

export const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  /* clip (not hidden) so horizontal decor peeks are clipped but vertical
     peeks (top: -100px, bottom: -80px) stay visible */
  overflow-x: clip;
`;

export const EmptyState = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 32px;
  width: 100%;
  max-width: 860px;
  min-height: 320px;
  margin: ${(props) => (props.$isFeedView ? "198px auto 48px" : "48px auto")};
  text-align: center;
  border-radius: var(--radius-xl);
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.95) 0%,
      rgba(var(--glass-pale-rgb), 0.86) 55%,
      rgba(var(--glass-pale-rgb), 0.76) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.9) 0%,
      rgba(var(--glass-pale-rgb), 0.84) 100%
    );
  backdrop-filter: blur(26px) saturate(180%);
  -webkit-backdrop-filter: blur(26px) saturate(180%);
  box-shadow:
    0 28px 56px -18px rgba(var(--color-brand-500-rgb), 0.36),
    0 10px 24px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -8px 20px rgba(var(--color-brand-200-rgb), 0.24);
  overflow: hidden;
  isolation: isolate;
  animation: ${emptyStateFloat} 5.5s ease-in-out infinite;

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 24px 48px -16px rgba(0, 0, 0, 0.55),
      0 8px 18px rgba(0, 0, 0, 0.32),
      inset 0 -8px 20px rgba(var(--color-brand-700-rgb), 0.16);
    &::after {
      opacity: 0;
    }
  }

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 25%;
    right: 25%;
    height: 14px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.4) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  h3 {
    margin-bottom: 12px;
    font-family: var(--font-family-primary);
    font-size: 28px;
    font-weight: 800;
    line-height: normal;
    letter-spacing: -0.02em;

    /* Brand gradient heading */
    background: linear-gradient(
      0deg,
      var(--color-text-strong) 0%,
      var(--color-text-body) 45%,
      var(--color-brand-600) 100%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
  }

  p {
    margin-bottom: 24px;
    max-width: 460px;
    color: var(--color-text-body);
    font-family: var(--font-family-primary);
    font-size: 16px;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.005em;
  }

  @media (max-width: 768px) {
    margin: ${(props) => (props.$isFeedView ? "120px auto 32px" : "32px auto")};
  }
`;

export const Button = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: ${(props) => (props.$width ? "100%" : "180px")};
  min-height: 46px;
  padding: 0 22px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.32),
    0 3px 8px rgba(var(--color-brand-600-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.28);

  [data-theme="dark"] & {
    border: none;
    border-color: transparent;
    box-shadow:
      0 8px 18px rgba(0, 0, 0, 0.5),
      0 3px 8px rgba(0, 0, 0, 0.3),
      inset 0 -3px 6px rgba(0, 0, 0, 0.22);
  }

  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 220ms var(--ease-snappy),
    filter 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    [data-theme="dark"] &:hover {
      border-color: transparent;
      border: none;
      box-shadow:
        0 8px 18px rgba(0, 0, 0, 0.5),
        0 3px 8px rgba(0, 0, 0, 0.3),
        inset 0 -3px 6px rgba(0, 0, 0, 0.22);
    }
    box-shadow:
      0 18px 34px rgba(var(--color-brand-600-rgb), 0.44),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
  }
`;
